import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';

/**
 * Fixed Routes KML Generator - Handles conversion of fixed routes data to/from KML format
 * Integrates fixed routes data into the existing map saving system
 */

/**
 * Serialize fixed routes data to KML-compatible format
 * @param routes Array of fixed route objects to serialize
 * @param stops Array of fixed route stop objects to serialize
 * @returns KML fragment containing fixed routes data in ExtendedData
 */
export function serializeFixedRoutesToKml(routes: FixedRoute[], stops: FixedRouteStop[]): string {
  if (routes.length === 0 && stops.length === 0) return '';

  // Persist the full route/stop objects, stripping only runtime-only fields.
  // This preserves optimised-route metadata (simulationImport, tripSchedules, gtfsRouteId,
  // full geometry, UAT/GTFS fields, etc.) so reopened maps reload in the proper format.
  const optimizedRoutes = routes.map(route => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { lineId, layerId, ...persisted } = route;
    const optimized: any = { ...persisted };

    // CRITICAL: Always preserve color - distinguish "no color set" from "color explicitly cleared".
    // If color is undefined/null, save as null so loader knows to use a generated color.
    if (route.color === undefined || route.color === null) {
      optimized.color = null;
      console.log(`💾 Saving route "${route.name}" with color: null (will use generated color)`);
    } else {
      console.log(`💾 Saving route "${route.name}" with color:`, route.color || '(empty string)');
    }

    return optimized;
  });

  const optimizedStops = stops.map(stop => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { mapPointId, ...persisted } = stop;
    return persisted;
  });

  // Convert to JSON and encode for KML storage
  const fixedRoutesData = {
    routes: optimizedRoutes,
    stops: optimizedStops,
    version: '1.0'
  };
  
  const jsonData = JSON.stringify(fixedRoutesData, null, 2);
  const encodedData = encodeURIComponent(jsonData);
  
  return `
    <Folder>
      <name>Fixed Routes Configuration</name>
      <ExtendedData>
        <Data name="fixed_routes_data">
          <value>${encodedData}</value>
        </Data>
        <Data name="fixed_routes_version">
          <value>1.0</value>
        </Data>
      </ExtendedData>
    </Folder>
  `;
}

/**
 * Deserialize fixed routes data from KML format
 * @param kmlData KML string containing fixed routes data
 * @returns Object with routes and stops arrays, or empty arrays if no data found
 */
export function deserializeFixedRoutesFromKml(kmlData: string): { routes: FixedRoute[]; stops: FixedRouteStop[] } {
  try {
    // Extract fixed routes ExtendedData from KML
    const fixedRoutesElement = extractFixedRoutesElement(kmlData);
    if (!fixedRoutesElement) {
      return { routes: [], stops: [] };
    }
    
    // Decode and parse JSON data
    const encodedData = extractFixedRoutesJson(fixedRoutesElement);
    if (!encodedData) {
      return { routes: [], stops: [] };
    }
    
    let decodedData: string;
    try {
      decodedData = decodeURIComponent(encodedData);
    } catch (decodeError) {
      console.warn('Failed to decode fixed routes data:', decodeError);
      return { routes: [], stops: [] };
    }
    
    let fixedRoutesData: any;
    try {
      fixedRoutesData = JSON.parse(decodedData);
    } catch (parseError) {
      console.warn('Failed to parse fixed routes JSON:', parseError);
      return { routes: [], stops: [] };
    }
    
    // Validate and return fixed routes data
    return validateFixedRoutesData(fixedRoutesData);
  } catch (error) {
    console.warn('Failed to parse fixed routes data:', error);
    return { routes: [], stops: [] };
  }
}

/**
 * Extract fixed routes folder element from KML data
 * @param kmlData KML string to search
 * @returns Fixed routes folder element or null if not found
 */
function extractFixedRoutesElement(kmlData: string): Element | null {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlData, 'text/xml');
  
  // Look for folder with name "Fixed Routes Configuration"
  const folders = xml.querySelectorAll('Folder');
  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i];
    const nameElement = folder.querySelector('name');
    if (nameElement?.textContent === 'Fixed Routes Configuration') {
      return folder;
    }
  }
  
  return null;
}

/**
 * Extract fixed routes JSON data from fixed routes element
 * @param fixedRoutesElement Fixed routes folder element
 * @returns Encoded fixed routes JSON string
 */
function extractFixedRoutesJson(fixedRoutesElement: Element): string {
  const dataElements = fixedRoutesElement.querySelectorAll('ExtendedData Data');
  for (let i = 0; i < dataElements.length; i++) {
    const dataElement = dataElements[i];
    const nameAttr = dataElement.getAttribute('name');
    if (nameAttr === 'fixed_routes_data') {
      const valueElement = dataElement.querySelector('value');
      return valueElement?.textContent || '';
    }
  }
  return '';
}

/**
 * Validate fixed routes data structure
 * @param data Parsed fixed routes data
 * @returns Validated fixed routes and stops arrays
 */
function validateFixedRoutesData(data: any): { routes: FixedRoute[]; stops: FixedRouteStop[] } {
  // Ensure data has routes and stops arrays
  if (!data || typeof data !== 'object') {
    console.warn('Fixed routes data is not an object:', data);
    return { routes: [], stops: [] };
  }

  const routes = Array.isArray(data.routes) ? data.routes : [];
  const stops = Array.isArray(data.stops) ? data.stops : [];

  // Validate routes and preserve all fields, including optimised-route metadata
  // (simulationImport, tripSchedules, gtfsRouteId, full geometry, UAT/GTFS fields, etc.)
  const validRoutes = routes.map((route: any) => {
    if (!route || typeof route !== 'object') {
      console.warn('Invalid route object:', route);
      return null;
    }

    if (!route.id || !route.name || !Array.isArray(route.stops) || !Array.isArray(route.segments)) {
      console.warn('Route missing required fields:', route);
      return null;
    }

    if (route.stops.length < 2) {
      console.warn('Route has less than 2 stops:', route);
      return null;
    }

    // Spread all persisted fields, then normalize a few.
    const restoredRoute: any = { ...route };

    // Color: null in saved data means "use generated color" — drop it so callers fall back.
    if (route.color === undefined || route.color === null) {
      delete restoredRoute.color;
      console.log(`🔄 Restoring route "${route.name}" with color: null (will use generated color)`);
    } else {
      console.log(`🔄 Restoring route "${route.name}" with color:`, route.color || '(empty string)');
    }

    if (route.geometry?.coordinates?.length) {
      console.log(`🔄 Restoring route "${route.name}" with saved geometry (${route.geometry.coordinates.length} points)`);
    }

    // Filter waypoints to the strict [number, number][] format.
    if (Array.isArray(route.waypoints) && route.waypoints.length > 0) {
      const validWaypoints = route.waypoints.filter((wp: any) =>
        Array.isArray(wp) &&
        wp.length === 2 &&
        typeof wp[0] === 'number' &&
        typeof wp[1] === 'number' &&
        !isNaN(wp[0]) &&
        !isNaN(wp[1])
      ) as [number, number][];

      if (validWaypoints.length > 0) {
        restoredRoute.waypoints = validWaypoints;
        console.log(`🔄 Restoring route "${route.name}" with saved waypoints (${validWaypoints.length} waypoints)`);
      } else {
        delete restoredRoute.waypoints;
      }
    }

    return restoredRoute as FixedRoute;
  }).filter((route: FixedRoute | null): route is FixedRoute => route !== null);

  // Stops: keep all fields, validate required ones only
  const validStops = stops.filter((stop: any) => {
    if (!stop || typeof stop !== 'object') {
      console.warn('Invalid stop object:', stop);
      return false;
    }

    if (!stop.id || !stop.name || !stop.coordinates) {
      console.warn('Stop missing required fields:', stop);
      return false;
    }

    if (!stop.coordinates.lat || !stop.coordinates.lng) {
      console.warn('Stop missing coordinates:', stop);
      return false;
    }

    return true;
  }) as FixedRouteStop[];

  return { routes: validRoutes, stops: validStops };
}

/**
 * Combine layer KML, fleet KML, and fixed routes KML into a single KML document
 * @param layerKml KML string for map layers
 * @param fleetKml KML string for fleet data
 * @param fixedRoutesKml KML string for fixed routes data
 * @returns Combined KML string
 */
export function combineKmlDataWithFixedRoutes(layerKml: string, fleetKml: string, fixedRoutesKml: string): string {
  let combinedKml = layerKml;
  
  // Add fleet KML if present
  if (fleetKml.trim()) {
    const documentEndIndex = combinedKml.lastIndexOf('</Document>');
    if (documentEndIndex !== -1) {
      combinedKml = combinedKml.slice(0, documentEndIndex) + fleetKml + combinedKml.slice(documentEndIndex);
    }
  }
  
  // Add fixed routes KML if present
  if (fixedRoutesKml.trim()) {
    const documentEndIndex = combinedKml.lastIndexOf('</Document>');
    if (documentEndIndex !== -1) {
      combinedKml = combinedKml.slice(0, documentEndIndex) + fixedRoutesKml + combinedKml.slice(documentEndIndex);
    }
  }
  
  return combinedKml;
}

